// Auto-extracted from Profile.jsx
import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { C } from "../../lib/theme";
import { Card } from "../../components/Card";
import { Btn } from "../../components/Btn";
import { Eyebrow } from "../../components/Eyebrow";
import { Icon } from "../../components/Icon";
import { Skeleton } from "../../components/Skeleton";
import { EmptyState } from "../../components/EmptyState";
import { ProductCard } from "../../components/ProductCard";
import { supabase } from "../../lib/supabase";
import { fmtK } from "../../lib/money";
import { useWishlist } from "../../store/wishlistStore";
import { getAllLocations } from "../../data/zambianLocations";
import { timeAgo } from "../../lib/dateUtils";
import { useCategories } from "../../hooks/useCategories";
import { BecomeSellerModal } from "../../components/BecomeSellerModal";
import { PhoneVerificationModal } from "../../components/PhoneVerificationModal";
import { DeleteAccountModal } from "../../components/DeleteAccountModal";
import { Field, SecurityRow, InfoRow, inputStyle } from "./shared";

export function WishlistPanel({ user }) {
  // Query the wishlist table directly rather than relying on the store alone —
  // the store is populated asynchronously on auth hydrate, and this panel must
  // show items regardless of whether that load has completed. We still subscribe
  // to the store so un-hearting a product (in this panel or elsewhere) removes
  // it from view without a refetch.
  const map = useWishlist((s) => s.map);
  const loadStore = useWishlist((s) => s.load);

  const { data: products = [], isLoading: loading } = useQuery({
    queryKey: ["wishlist-products", user?.id],
    enabled: !!user?.id,
    staleTime: 60_000,
    queryFn: async () => {
      const { data: wishes } = await supabase
        .from("wishlist")
        .select("product_id")
        .eq("user_id", user.id);
      const ids = (wishes || []).map((w) => w.product_id);
      if (!ids.length) return [];
      // Also sync the Zustand store so ProductCard hearts render correctly here.
      loadStore();
      const { data } = await supabase
        .from("products")
        .select("id, title, price, compare_price, images, seller_id, category_id, stock_qty, created_at, status, sellers(store_name, address), category:categories(name, icon)")
        .in("id", ids)
        .is("deleted_at", null);
      return data || [];
    },
  });

  // Hide items that have been un-hearted while this panel is open, without
  // having to refetch from Supabase. If the store hasn't populated yet (empty
  // map), show everything we just fetched so the panel isn't momentarily blank.
  const visible = useMemo(() => {
    const storeIds = Object.keys(map);
    if (storeIds.length === 0) return products;
    return products.filter((p) => map[p.id] === true);
  }, [products, map]);

  if (loading) {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
        {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} h={240} r={16} />)}
      </div>
    );
  }
  if (visible.length === 0) {
    return <EmptyState icon="heart" title="No wishlisted items" body="Tap the heart on any product to save it here." />;
  }
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14 }}>
      {visible.map((p) => <ProductCard key={p.id} product={p} />)}
    </div>
  );
}
